import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/PartnerAgencyBusinessAssociates.graphql';
import { Query, QuerypartnerAgencyBusinessAssociatesArgs } from '../../__generated__/globalTypes';

export const usePartnerAgencyBusinessAssociatesQuery = (
  options?: QueryHookOptions<Query, QuerypartnerAgencyBusinessAssociatesArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerypartnerAgencyBusinessAssociatesArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const usePartnerAgencyBusinessAssociatesLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerypartnerAgencyBusinessAssociatesArgs>
): QueryResult<Query, QuerypartnerAgencyBusinessAssociatesArgs> & {
  getPartnerAgencyBusinessAssociates: LazyQueryExecFunction<Query, QuerypartnerAgencyBusinessAssociatesArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getPartnerAgencyBusinessAssociates, result] = useLazyQuery<Query, QuerypartnerAgencyBusinessAssociatesArgs>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getPartnerAgencyBusinessAssociates, ...result };
};

export const usePartnerAgencyBusinessAssociatesPromiseQuery = () => {
  const client = useApolloClient();

  const getPartnerAgencyBusinessAssociates = useCallback(
    (options?: Omit<QueryOptions<QuerypartnerAgencyBusinessAssociatesArgs, Query>, 'query'>) =>
      client.query<Query, QuerypartnerAgencyBusinessAssociatesArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getPartnerAgencyBusinessAssociates };
};
// Suspense Query Hooks
export const usePartnerAgencyBusinessAssociatesSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerypartnerAgencyBusinessAssociatesArgs>
) => {
  const result = useSuspenseQuery<Query, QuerypartnerAgencyBusinessAssociatesArgs>(QUERY, {
    ...options
  });

  return result;
};
